// Build Supabase select query string from DataColumn dependencies.
// Pure function — no hooks, no state. Testable and reusable.
//
// Generates queries like: "id,name,user:user_id(id,name,profile:profile_id(avatar))"

import type { DataColumn, ListColumnSpec } from "../../../ds/tables/types";

/**
 * Build a Supabase select string from visible columns' deps.
 * Accepts both `DataColumn<T>` (table-context, requires render) and the
 * plain `ListColumnSpec<T>` shape (schema picks). Only reads `col.deps`.
 * Returns undefined if only "id" is needed (let Supabase return all fields).
 *
 * `idColumn` — the main table's PK when it is not `id` (SchemaMeta.idColumn).
 * The select then seeds `id:<col>,<col>` — the PostgREST alias synthesizes a
 * runtime `id` so rowKey / selection / row testids keep working — and never
 * returns undefined (a bare `select=*` would drop the alias).
 */
export function buildSelectString<T>(
  columns: ReadonlyArray<DataColumn<T> | ListColumnSpec<T, unknown>>,
  permanentFields?: string[],
  idColumn: string = "id",
): string | undefined {
  const idSeed = idColumn === "id" ? ["id"] : [`id:${idColumn}`, idColumn];
  const mainFields = new Set<string>([...idSeed, ...(permanentFields ?? [])]);
  const relationFields = new Map<string, Set<string>>();
  const nestedRelations = new Map<string, Map<string, Set<string>>>();
  const relationMetadata = new Map<
    string,
    { foreignKey: string; pk?: string; nested?: Record<string, string> }
  >();

  for (const col of columns) {
    // Client-mode sort reads the sort field off the fetched row.
    if (col.sortField) mainFields.add(col.sortField);
    if (!col.deps) continue;

    col.deps.fields?.forEach((f) => mainFields.add(f.split("!")[0]));

    if (col.deps.relations) {
      for (const [key, fields] of Object.entries(col.deps.relations)) {
        let bucket = relationFields.get(key);
        if (!bucket) {
          bucket = new Set(["id"]);
          relationFields.set(key, bucket);
        }
        fields.forEach((f) => bucket.add(f));
      }
    }

    if (col.deps.nested) {
      for (const [key, nestedMap] of Object.entries(col.deps.nested)) {
        if (!relationFields.has(key)) relationFields.set(key, new Set(["id"]));
        let nestedBucket = nestedRelations.get(key);
        if (!nestedBucket) {
          nestedBucket = new Map();
          nestedRelations.set(key, nestedBucket);
        }
        for (const [nk, nf] of Object.entries(nestedMap)) {
          let fieldSet = nestedBucket.get(nk);
          if (!fieldSet) {
            fieldSet = new Set(["id"]);
            nestedBucket.set(nk, fieldSet);
          }
          nf.forEach((f) => fieldSet.add(f));
        }
      }
    }

    if (col.deps.foreignKeys) {
      for (const [key, meta] of Object.entries(col.deps.foreignKeys)) {
        const existing = relationMetadata.get(key);
        if (!existing) {
          relationMetadata.set(key, meta);
        } else if (meta.nested) {
          relationMetadata.set(key, { ...existing, nested: { ...existing.nested, ...meta.nested } });
        }
      }
    }
  }

  // Build relation parts
  const relationParts: string[] = [];
  for (const [key, fields] of relationFields) {
    const meta = relationMetadata.get(key);
    let foreignKey = meta?.foreignKey ?? `${key}_id`;
    let hint = "";
    if (foreignKey.includes("!")) {
      const [k, mod] = foreignKey.split("!");
      foreignKey = k;
      hint = `!${mod}`;
    }

    // Targets whose primary key is not `id` (e.g. fakturownia_payment, pk
    // external_payment_id) declare it via deps.foreignKeys[..].pk — seeding a
    // non-existent `id` into the embed makes PostgREST 400 the WHOLE query.
    const embedFields = meta?.pk
      ? new Set<string>([meta.pk, ...[...fields].filter((f) => f !== "id")])
      : fields;

    const nested = nestedRelations.get(key);
    const fieldStr = Array.from(embedFields).join(",");

    if (nested && nested.size > 0) {
      const nestedParts: string[] = [];
      for (const [nk, nf] of nested) {
        const nfk = relationMetadata.get(key)?.nested?.[nk] ?? `${nk}_id`;
        nestedParts.push(`${nk}:${nfk}(${Array.from(nf).join(",")})`);
      }
      relationParts.push(`${key}:${foreignKey}${hint}(${fieldStr},${nestedParts.join(",")})`);
    } else {
      relationParts.push(`${key}:${foreignKey}${hint}(${fieldStr})`);
    }
  }

  if (mainFields.size === 1 && relationParts.length === 0) return undefined;

  const parts = [Array.from(mainFields).join(","), relationParts.join(",")].filter(Boolean);
  return parts.join(",");
}
